import classNames from 'classnames';
import { ComponentProps, MutableRefObject, useEffect, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import setDenaliAttribute from '../../utils/setDenaliAttribute';
import IconButton from '../IconButton';
import styles from './styles/styles.module.scss';

const singleLineHeight = 24; // 16px font-size, 1.5 line-height
const textAreaPadding = 24;

type SubmitInputProps = {
  autoFocus?: boolean;
  characterLimit?: number;
  className?: string;
  disabled?: boolean;
  inputRef?: MutableRefObject<HTMLInputElement | HTMLTextAreaElement | null>;
  labelText?: string;
  loading?: boolean;
  multiline?: boolean;
  onChange: (value: string) => void;
  onSubmit: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  /** Multiline only: Enter submits and Shift+Enter adds a line, the way chat inputs work. */
  submitOnEnter?: boolean;
  submitIcon: ComponentProps<typeof IconButton>['icon'];
  value?: string;
};

export default function SubmitInput({
  autoFocus,
  characterLimit,
  className,
  disabled,
  inputRef,
  labelText,
  loading,
  multiline = false,
  onChange,
  onSubmit,
  placeholder,
  required = false,
  submitOnEnter = false,
  submitIcon,
  value
}: SubmitInputProps) {
  const textArea = useRef<HTMLTextAreaElement | null>(null);
  const intl = useIntl();

  const updateRows = () => {
    if (textArea.current) {
      textArea.current.setAttribute('rows', '1');
      const textHeight = textArea.current.scrollHeight - textAreaPadding;
      const rows = Math.min(Math.max(Math.ceil(textHeight / singleLineHeight), 1), 5);
      textArea.current.setAttribute('rows', String(rows));
    }
  };

  // Resize when the value changes from outside, so it shrinks back when cleared (ex: after a submit).
  useEffect(updateRows, [value]);

  const overCharacterLimit = Boolean(characterLimit && value && value.length > characterLimit);
  const canSubmit = !disabled && !loading && !overCharacterLimit;

  const inputElement = multiline ? (
    <textarea
      ref={el => {
        textArea.current = el;
        if (inputRef) {
          inputRef.current = el;
        }
      }}
      aria-label={labelText || placeholder}
      autoFocus={autoFocus}
      className={styles.textArea}
      onChange={e => {
        onChange(e.target.value);
        updateRows();
        textArea.current?.scrollIntoView();
      }}
      onKeyDown={e => {
        if (submitOnEnter && e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
          e.preventDefault();
          if (canSubmit) {
            onSubmit(value || '');
          }
        }
      }}
      placeholder={placeholder}
      required={required}
      value={value}
    />
  ) : (
    <input
      aria-label={labelText || placeholder}
      autoFocus={autoFocus}
      className={styles.input}
      onChange={e => {
        onChange(e.target.value);
      }}
      placeholder={placeholder}
      required={required}
      value={value}
    />
  );

  return (
    <label {...setDenaliAttribute('SubmitInput')} className={className}>
      {labelText && <span className={styles.labelText}>{labelText}</span>}
      <div className={classNames(styles.inputContainer, overCharacterLimit && styles.hasError)}>
        {inputElement}
        <IconButton
          className={styles.button}
          disabled={disabled || overCharacterLimit}
          icon={submitIcon}
          loading={loading}
          onClick={() => onSubmit(value || '')}
          size="sm"
          title={intl.formatMessage({ defaultMessage: 'Submit' })}
          type="submit"
          variant="primary"
        />
      </div>
      {!disabled && overCharacterLimit && (
        <div className={styles.characterLimitError}>
          <span>
            <FormattedMessage defaultMessage="Character limit exceeded" />
          </span>
          <span>
            {value?.length}/{characterLimit}
          </span>
        </div>
      )}
    </label>
  );
}
